export type ExecutionCallback<T> = () => Promise<T>

/**
 * Limits how many async executions can be in flight at once. Unlike a rate limiter
 * (max N per second), this caps concurrency (max N running at the same time), which is
 * what's needed to avoid ERR_INSUFFICIENT_RESOURCES when firing off requests for a large
 * batch of resources (e.g. recording segments) all at once.
 */
export class ConcurrencyThrottle {
  // Did some testing on different values here. 1500+ gives zero value from throttling
  // (effectively unthrottled for this engagement's segment count) and still errors out.
  // 800 is the highest value tested that avoids the insufficient resources error while
  // meaningfully improving load time over lower values.
  public static DEFAULT_MAX_CONCURRENT = 800

  private active = 0
  private readonly queue: (() => void)[] = []

  constructor(private readonly maxConcurrent = ConcurrencyThrottle.DEFAULT_MAX_CONCURRENT) {}

  public async throttle<T>(callback: ExecutionCallback<T>): Promise<T> {
    await this.acquire()
    try {
      return await callback()
    } finally {
      this.release()
    }
  }

  private acquire(): Promise<void> {
    if (this.active < this.maxConcurrent) {
      this.active++
      return Promise.resolve()
    }

    return new Promise<void>(resolve => {
      this.queue.push(() => {
        this.active++
        resolve()
      })
    })
  }

  private release(): void {
    this.active--
    const next = this.queue.shift()
    if (next) {
      next()
    }
  }
}
